import { useState } from 'react';
import { Phone, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

interface PhoneCellProps {
  value: string | null;
  onChange: (value: string | null) => void;
  readOnly?: boolean;
}

export function PhoneCell({ value, onChange, readOnly }: PhoneCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value || '');

  const formatPhone = (phone: string) => {
    // Remove tudo que não é número
    const numbers = phone.replace(/\D/g, '');
    
    // Formata conforme quantidade de dígitos
    if (numbers.length <= 10) {
      // Fixo: (XX) XXXX-XXXX
      return numbers.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
    } else {
      // Celular: (XX) XXXXX-XXXX
      return numbers.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setTempValue(formatted);
  };

  const handleSave = () => {
    const trimmed = tempValue.trim();
    if (!trimmed) {
      onChange(null);
      setIsEditing(false);
      return;
    }

    onChange(trimmed);
    setIsEditing(false);
  };

  const handleClear = () => {
    setTempValue('');
    onChange(null);
    setIsEditing(false);
  };

  const getPhoneLink = (phone: string) => {
    const numbers = phone.replace(/\D/g, '');
    return `tel:+55${numbers}`;
  };

  if (readOnly && !value) {
    return <span className="text-gray-400 text-sm">-</span>;
  }

  if (readOnly && value) {
    return (
      <a
        href={getPhoneLink(value)}
        className="flex items-center gap-2 text-blue-600 hover:text-blue-800 hover:underline"
      >
        <Phone className="w-4 h-4" />
        <span className="truncate">{value}</span>
      </a>
    );
  }

  if (isEditing) {
    return (
      <div className="space-y-2 p-2 bg-white border rounded shadow-lg min-w-[300px]">
        <Input
          type="tel"
          placeholder="(11) 99999-9999"
          value={tempValue}
          onChange={handleChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') setIsEditing(false);
          }}
          maxLength={15}
          autoFocus
        />
        <div className="flex gap-2">
          <Button size="sm" onClick={handleSave}>
            Salvar
          </Button>
          <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
            Cancelar
          </Button>
          {value && (
            <Button size="sm" variant="ghost" onClick={handleClear}>
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (!value) {
    return (
      <div
        className="text-gray-400 text-sm cursor-pointer hover:text-gray-600"
        onClick={() => setIsEditing(true)}
      >
        Adicionar telefone
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
      onClick={() => setIsEditing(true)}
    >
      <a
        href={getPhoneLink(value)}
        className="flex items-center gap-2 text-blue-600 hover:text-blue-800 hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        <Phone className="w-4 h-4" />
        <span className="truncate">{value}</span>
      </a>
    </div>
  );
}
